import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StickyNote, MessageSquareText, Send, User, CalendarClock, Inbox, AtSign } from "lucide-react";
import { DataActionsContext } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NotesCell — renders a small icon button in the stage table's Notes column.
 * Empty state: outlined inbox (grey). Populated: filled-note icon with a
 * small red counter badge in the top-right. Click → opens NotesDialog.
 *
 * Notes are stored per line-item as an array on `item.user_notes`, shaped:
 *   { id, text, eta?, author, author_role, created_at }
 */
export default function NotesCell({ item, canEdit = true, showCaption = false }) {
  const [open, setOpen] = useState(false);
  const notes = Array.isArray(item.user_notes) ? item.user_notes : [];
  const count = notes.length;
  // Red only when at least one note was written while the item was in its current stage.
  // Grey when notes exist but all were made in a different stage (or stage unknown on legacy notes).
  const hasCurrentStageNote = notes.some(n => n.stage && n.stage === item.stage);
  // Latest note = most recent created_at. Falls back to last array element so
  // legacy notes without timestamps still surface a preview.
  const latest = useMemo(() => {
    if (count === 0) return null;
    const sorted = [...notes].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return sorted[0];
  }, [notes, count]);
  const ariaLabel = count === 0
    ? "Add a note"
    : `${count} note${count === 1 ? "" : "s"} — click to view or add`;

  // Caption tone tracks the icon: quiet amber when a note was added in THIS
  // stage (the actionable case), muted otherwise — so it reads as part of the
  // same DETAILS / BACK / PROBLEM column, not a stray control.
  const captionCls = count > 0 && hasCurrentStageNote
    ? "text-amber-600/80 dark:text-amber-400/80"
    : "text-[hsl(var(--muted-foreground))]/70";

  return (
    <>
      <div className="flex flex-col items-center">
      <TooltipProvider delayDuration={150} skipDelayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className={`relative inline-flex items-center justify-center w-7 h-7 rounded-full rl-well transition-colors ${
                count === 0
                  ? "text-[hsl(var(--muted-foreground))]/60 hover:text-[hsl(var(--foreground))]"
                  : hasCurrentStageNote
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))]"
              }`}
              aria-label={ariaLabel}
              data-testid={`notes-cell-${item.id}`}
            >
              <MessageSquareText className="w-4 h-4" strokeWidth={count === 0 ? 1.6 : 2} />
              {count > 0 && (
                <span
                  className={`absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-[3px] rounded-full text-white text-[8.5px] font-bold leading-none ring-[1.5px] ring-[hsl(var(--card))] ${
                    hasCurrentStageNote ? "bg-red-500" : "bg-[hsl(var(--muted-foreground))]/55"
                  }`}
                  data-testid={`notes-count-${item.id}`}
                >
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </button>
          </TooltipTrigger>
          {count === 0 ? (
            <TooltipContent
              side="top"
              sideOffset={6}
              className="bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] shadow-sm text-[10px] font-medium tracking-wide px-2 py-1"
            >
              Add a note
            </TooltipContent>
          ) : (
            <TooltipContent
              side="top"
              sideOffset={8}
              align="end"
              className="bg-[hsl(var(--popover))] text-[hsl(var(--foreground))] border border-amber-500/30 shadow-md p-0 max-w-[280px] animate-in fade-in-0 zoom-in-95 duration-150"
              data-testid={`notes-preview-${item.id}`}
            >
              <NotesPreviewCard note={latest} count={count} />
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      {showCaption && (
        <span className={`text-[8.5px] uppercase tracking-wide font-semibold leading-none mt-0.5 ${captionCls}`} aria-hidden="true">
          Notes
        </span>
      )}
      </div>

      <NotesDialog open={open} onOpenChange={setOpen} item={item} canEdit={canEdit} />
    </>
  );
}

/**
 * NotesPreviewCard — small bubble that surfaces the latest note inside the
 * Notes button's tooltip. Calm aesthetic: thin amber border (matches the note
 * accent), author + relative time on top, two-line clamped snippet, and the
 * "+N more" / "click to view" footer when extra notes are stacked.
 */
function NotesPreviewCard({ note, count }) {
  if (!note) return null;
  const text = (note.text || "").trim();
  // Strip trailing whitespace from each line for a cleaner clamp render.
  const snippet = text.length > 220 ? text.slice(0, 218).trimEnd() + "…" : text;
  const more = Math.max(0, count - 1);
  return (
    <div className="px-3 py-2.5 w-[260px]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
        <span className="inline-flex items-center gap-1">
          <span
            className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 text-[9px] font-bold"
            aria-hidden="true"
          >
            {((note.author || "?")[0] || "?").toUpperCase()}
          </span>
          <span className="font-semibold normal-case text-[hsl(var(--foreground))]">{note.author || "Unknown"}</span>
        </span>
        <span className="ml-auto tabular-nums text-[10px]">{formatRelative(note.created_at)}</span>
      </div>
      <p className="mt-1.5 text-[12px] leading-snug text-[hsl(var(--foreground))]/90 whitespace-pre-wrap break-words line-clamp-3">
        {snippet || <span className="italic text-[hsl(var(--muted-foreground))]">(empty note)</span>}
      </p>
      {note.eta && (
        <div className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[9px] font-semibold uppercase tracking-wider">
          <CalendarClock className="w-2.5 h-2.5" />
          <span>ETA · {formatEtaDate(note.eta)}</span>
        </div>
      )}
      <div className="mt-2 pt-2 border-t border-[hsl(var(--border))]/60 flex items-center justify-between text-[9px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
        <span>{more > 0 ? `+${more} more note${more === 1 ? "" : "s"}` : "Latest note"}</span>
        <span className="font-semibold text-amber-600 dark:text-amber-400">Click to view</span>
      </div>
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return "";
  }
}

function formatEtaDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return iso;
  }
}

/**
 * Render note text with `@Name` mentions highlighted as amber chips.
 * We match any `@` followed by up to 3 capitalised words (e.g. "@Tushar Dhawan").
 */
function renderNoteText(text, mentionables = []) {
  if (!text) return null;
  const nameIndex = new Map(mentionables.map(m => [(m.name || "").toLowerCase(), m]));
  const parts = [];
  const re = /@([A-Za-z][A-Za-z0-9._'-]*(?:\s+[A-Za-z][A-Za-z0-9._'-]*){0,2})/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const raw = m[1];
    // Greedy → shrink from 3 words to 1 until a user matches
    const words = raw.trim().split(/\s+/);
    let matched = null;
    for (let n = Math.min(words.length, 3); n >= 1; n--) {
      const attempt = words.slice(0, n).join(" ").toLowerCase();
      if (nameIndex.has(attempt)) { matched = { name: words.slice(0, n).join(" "), tail: words.slice(n).join(" ") }; break; }
    }
    if (matched) {
      parts.push(
        <span key={`m-${m.index}`} className="inline-flex items-center px-1 py-0 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold text-[12px]">
          @{matched.name}
        </span>
      );
      if (matched.tail) parts.push(" " + matched.tail);
    } else {
      parts.push("@" + raw);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function NotesDialog({ open, onOpenChange, item, canEdit }) {
  const { addUserNote } = useContext(DataActionsContext) || {};
  const [draftText, setDraftText] = useState("");
  const [draftEta, setDraftEta] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mentionables, setMentionables] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionAnchor, setMentionAnchor] = useState(null);     // caret pos of `@`
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);

  const notes = Array.isArray(item.user_notes) ? item.user_notes : [];
  const sortedNotes = [...notes].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  // Lazy-load mentionable users the first time the dialog opens
  useEffect(() => {
    if (!open || mentionables.length > 0) return;
    axios.get(`${API}/users/mentionables`).then(r => setMentionables(r.data || [])).catch(() => { });
  }, [open, mentionables.length]);

  useEffect(() => {
    if (open) {
      setDraftText("");
      setDraftEta("");
      setMentionOpen(false);
    }
  }, [open]);

  const filteredMentions = useMemo(() => {
    const q = (mentionQuery || "").toLowerCase().trim();
    if (!q) return mentionables.slice(0, 6);
    return mentionables
      .filter(u => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionables, mentionQuery]);

  const handleDraftChange = (e) => {
    const val = e.target.value;
    setDraftText(val);
    const caret = e.target.selectionStart ?? val.length;
    // Detect `@token` right before the caret
    const before = val.slice(0, caret);
    const atMatch = before.match(/(^|\s)@([A-Za-z][A-Za-z0-9._'-]{0,30})?$/);
    if (atMatch) {
      const atPos = caret - (atMatch[2] || "").length - 1;
      setMentionAnchor(atPos);
      setMentionQuery(atMatch[2] || "");
      setMentionIndex(0);
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
      setMentionAnchor(null);
    }
  };

  const applyMention = (user) => {
    if (mentionAnchor == null || !textareaRef.current) return;
    const caret = textareaRef.current.selectionStart ?? draftText.length;
    const before = draftText.slice(0, mentionAnchor);
    const after = draftText.slice(caret);
    const insertion = `@${user.name || user.email.split("@")[0]} `;
    const next = before + insertion + after;
    setDraftText(next);
    setMentionOpen(false);
    setMentionAnchor(null);
    // Restore caret just after the insertion
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      const pos = before.length + insertion.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleTextareaKey = (e) => {
    if (mentionOpen && filteredMentions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex(i => (i + 1) % filteredMentions.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex(i => (i - 1 + filteredMentions.length) % filteredMentions.length); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        applyMention(filteredMentions[mentionIndex]);
        return;
      }
      if (e.key === "Escape") { setMentionOpen(false); return; }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const txt = draftText.trim();
    if (!txt || submitting) return;
    setSubmitting(true);
    try {
      await addUserNote(item.id, { text: txt, eta: draftEta || null });
      setDraftText("");
      setDraftEta("");
      setMentionOpen(false);
    } catch {
      /* toast already shown by mutation */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[540px] max-w-[94vw] max-h-[82vh] flex flex-col p-0 overflow-hidden" data-testid={`notes-dialog-${item.id}`}>
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[hsl(var(--border))]/60">
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
            <StickyNote className="w-4 h-4 text-amber-500" />
            <span>Notes</span>
            <span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))] truncate">
              · {item.part_number || item.po_number || item.id.slice(0, 8)}
            </span>
            {notes.length > 0 && (
              <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">
                {notes.length} note{notes.length === 1 ? "" : "s"}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[hsl(var(--muted-foreground))]">
            Free-form notes pinned to this line item. Type <span className="font-mono-data">@</span> to ping a teammate. ETA is optional.
          </DialogDescription>
        </DialogHeader>

        {/* Notes list */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-3 space-y-3 min-h-[140px]">
          {sortedNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-[hsl(var(--muted-foreground))]" data-testid={`notes-empty-${item.id}`}>
              <Inbox className="w-10 h-10 opacity-30 mb-2" strokeWidth={1.2} />
              <p className="text-sm">No notes yet</p>
              <p className="text-[11px] mt-0.5">Add the first one below</p>
            </div>
          ) : (
            sortedNotes.map((n) => (
              <div
                key={n.id}
                className="rounded-md border border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/20 px-3 py-2.5"
                data-testid={`note-${n.id}`}
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span className="font-semibold normal-case text-[hsl(var(--foreground))]">{n.author || "Unknown"}</span>
                    {n.author_role && <span className="normal-case text-[hsl(var(--muted-foreground))]">· {n.author_role}</span>}
                  </span>
                  <span className="ml-auto tabular-nums">{formatRelative(n.created_at)}</span>
                </div>
                <p className="mt-1.5 text-[13px] text-[hsl(var(--foreground))] whitespace-pre-wrap break-words leading-relaxed">
                  {renderNoteText(n.text, mentionables)}
                </p>
                {n.eta && (
                  <div className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[10px] font-semibold uppercase tracking-wider">
                    <CalendarClock className="w-3 h-3" />
                    <span>ETA · {formatEtaDate(n.eta)}</span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add note footer */}
        {canEdit && (
          <div className="border-t border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/30 px-5 py-3 space-y-2 relative">
            <div className="relative">
              <Textarea
                ref={textareaRef}
                value={draftText}
                onChange={handleDraftChange}
                onKeyDown={handleTextareaKey}
                placeholder="Add a note… type @ to mention someone"
                rows={2}
                className="resize-none text-[13px]"
                data-testid={`notes-draft-text-${item.id}`}
              />
              {mentionOpen && filteredMentions.length > 0 && (
                <div
                  className="absolute left-0 bottom-full mb-1 z-50 w-[260px] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-lg overflow-hidden"
                  data-testid="mention-popover"
                >
                  <div className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]/60 flex items-center gap-1.5">
                    <AtSign className="w-3 h-3" /> Mention teammate
                  </div>
                  {filteredMentions.map((u, idx) => (
                    <button
                      key={u.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); applyMention(u); }}
                      className={`w-full text-left px-2.5 py-1.5 flex items-center gap-2 ${
                        idx === mentionIndex ? "bg-amber-500/15" : "hover:bg-[hsl(var(--muted))]/40"
                      }`}
                    >
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[hsl(var(--muted))] text-[10px] font-bold text-[hsl(var(--foreground))] shrink-0">
                        {((u.name || u.email || "?")[0] || "?").toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-semibold text-[hsl(var(--foreground))] truncate">{u.name || u.email.split("@")[0]}</div>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">{u.role}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 flex-1">
                <CalendarClock className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                <Input
                  type="date"
                  value={draftEta}
                  onChange={(e) => setDraftEta(e.target.value)}
                  placeholder="Optional ETA"
                  className="h-8 text-xs w-[150px]"
                  data-testid={`notes-draft-eta-${item.id}`}
                />
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">ETA (optional)</span>
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!draftText.trim() || submitting}
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid={`notes-submit-${item.id}`}
              >
                <Send className="w-3 h-3" />
                {submitting ? "Adding…" : "Add note"}
              </button>
            </div>
            <p className="text-[9px] text-[hsl(var(--muted-foreground))]">Tip: press <kbd className="px-1 py-0 rounded border border-[hsl(var(--border))]">⌘</kbd>/<kbd className="px-1 py-0 rounded border border-[hsl(var(--border))]">Ctrl</kbd> + <kbd className="px-1 py-0 rounded border border-[hsl(var(--border))]">Enter</kbd> to submit.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
